'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import BaseAuctionCard from './BaseAuctionCard';
import { useFavorites } from '@/lib/hooks/useFavorites';
import { calculateTimeRemaining } from '@/lib/utils';

interface UpcomingAuctionCardProps {
  id: string;
  slug: string;
  title: string;
  location: string;
  startingPrice: number;
  image: string;
  startDate: string; // Status 1 (Yaklaşan) için başlama tarihi - müzayede ne zaman başlayacak
  timeLeft?: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  isFavorite?: boolean;
  onFavoriteToggle?: (id: string) => void;
  isAuthenticated?: boolean; // Server-side'dan gelecek
  priority?: boolean;
}

export default function UpcomingAuctionCard({
  id,
  slug,
  title,
  location,
  startingPrice,
  image,
  startDate,
  timeLeft,
  isFavorite = false,
  onFavoriteToggle,
  isAuthenticated = false,
  priority = false
}: UpcomingAuctionCardProps) {
  const t = useTranslations('auctions');
  const params = useParams();
  const { toggleFavorite, isLoading: favoriteLoading } = useFavorites();
  const [timeRemaining, setTimeRemaining] = useState(() => {
    // İlk hesaplama
    const calculated = calculateTimeRemaining(startDate);
    return {
      days: calculated.days,
      hours: calculated.hours,
      minutes: calculated.minutes,
      seconds: calculated.seconds,
      isExpired: calculated.isExpired
    };
  });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;
    
    const timer = setInterval(() => {
      const calculated = calculateTimeRemaining(startDate);
      setTimeRemaining({
        days: calculated.days,
        hours: calculated.hours,
        minutes: calculated.minutes,
        seconds: calculated.seconds,
        isExpired: calculated.isExpired
      });
      
      // Müzayede başladıysa timer'ı durdur
      if (calculated.isExpired) {
        clearInterval(timer);
        // Timer durduruldu, sayfa yenileme iptal edildi
      }
    }, 60000); // Her 1 dakikada güncelle

    return () => clearInterval(timer);
  }, [isClient, startDate, id]);

  const formatPrice = (price: number) => {
    if (!isClient) {
      // Server-side rendering için basit format
      return `${price.toLocaleString('tr-TR')} ₺`;
    }
    
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleFavoriteClick = async () => {
    // Eğer parent'tan onFavoriteToggle geliyorsa onu kullan, yoksa kendi hook'unu kullan
    if (onFavoriteToggle) {
      onFavoriteToggle(id);
    } else {
      try {
        await toggleFavorite(id, isFavorite);
      } catch (error) {
        // Error toggling favorite
      }
    }
  };

  // Client-side rendering için güvenli zaman gösterimi
  const displayTime = isClient ? timeRemaining : (timeLeft || { days: 0, hours: 0, minutes: 0 });

  const imageOverlay = (
    <div className="absolute top-4 left-4 bg-gray-600/80 backdrop-blur-sm rounded-full px-3 py-2">
      <div className="flex items-center gap-2 text-white">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm font-bold tabular-nums">{t('card.startsIn')}: {displayTime.days}g {displayTime.hours}s {displayTime.minutes}d</span>
      </div>
    </div>
  );

  const favoriteButton = isAuthenticated ? (
    <button
      onClick={handleFavoriteClick}
      disabled={favoriteLoading}
      className={`absolute top-4 right-4 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors ${
        favoriteLoading ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      {favoriteLoading ? (
        <div className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <svg 
          className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-current' : 'text-gray-600'}`} 
          fill={isFavorite ? 'currentColor' : 'none'} 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      )}
    </button>
  ) : null;

  const content = (
    <div className="flex justify-between items-center mb-4">
      <span className="text-gray-600 text-sm">{t('card.startingPrice')}</span>
      <span className="text-2xl font-bold text-gray-900 tabular-nums">{formatPrice(startingPrice)}</span>
    </div>
  );

  const actionButton = (
    <div className="mt-auto">
      <Link href={`/${params.locale}/muzayedeler/${slug}`}>
        <button className="w-full py-3 px-4 rounded-lg font-bold text-white bg-gray-400 hover:bg-gray-500 transition-colors">
          {t('card.viewDetails')}
        </button>
      </Link>
    </div>
  );

  return (
    <BaseAuctionCard
      id={id}
      title={title}
      location={location}
      image={image}
      isUpcoming={true}
      imageOverlay={imageOverlay}
      favoriteButton={favoriteButton}
      actionButton={actionButton}
      priority={priority}
    >
      {content}
    </BaseAuctionCard>
  );
}
